import type { RecurrenceRule } from '@/types/recurrence';

/**
 * Calculate the next due date based on a recurrence rule
 * @param currentDueDate The current due date
 * @param rule The recurrence rule
 * @param completionDate Optional completion date (used for "next from completion" logic)
 * @returns The next due date, or null if no more occurrences
 */
export const calculateNextDueDate = (
  currentDueDate: Date,
  rule: RecurrenceRule,
  completionDate?: Date
): Date | null => {
  const { frequency, interval, daysOfWeek, dayOfMonth, endDate } = rule;

  // Use completion date if provided and it's after current due date
  // This handles the case where task is completed late
  const baseDate =
    completionDate && completionDate > currentDueDate ? completionDate : currentDueDate;

  let nextDate = new Date(baseDate);

  switch (frequency) {
    case 'daily':
      nextDate.setDate(nextDate.getDate() + interval);
      break;

    case 'weekly':
      if (daysOfWeek && daysOfWeek.length > 0) {
        // Find the next day in the daysOfWeek array
        nextDate = getNextWeekdayOccurrence(baseDate, daysOfWeek, interval);
      } else {
        nextDate.setDate(nextDate.getDate() + 7 * interval);
      }
      break;

    case 'monthly':
      if (dayOfMonth) {
        // Set to specific day of month
        // Set date to 1 first to avoid auto-wrapping when changing month
        nextDate.setDate(1);
        nextDate.setMonth(nextDate.getMonth() + interval);
        nextDate.setDate(Math.min(dayOfMonth, getDaysInMonth(nextDate)));
      } else {
        // Keep same day of month
        const targetDay = baseDate.getDate();
        // Set date to 1 first to avoid auto-wrapping when changing month
        nextDate.setDate(1);
        nextDate.setMonth(nextDate.getMonth() + interval);
        // Handle months with fewer days
        nextDate.setDate(Math.min(targetDay, getDaysInMonth(nextDate)));
      }
      break;

    case 'yearly':
      // Handle leap year for Feb 29 - check before changing year
      const wasLeapDay = baseDate.getMonth() === 1 && baseDate.getDate() === 29;
      const newYear = nextDate.getFullYear() + interval;

      if (wasLeapDay && !isLeapYear(newYear)) {
        // Set to Feb 28 in non-leap year
        nextDate.setFullYear(newYear, 1, 28);
      } else {
        nextDate.setFullYear(newYear);
      }
      break;
  }

  // Check if we've passed the end date
  if (endDate && nextDate > endDate) {
    return null;
  }

  return nextDate;
};

/**
 * Get the next occurrence of a weekday from the given date
 */
const getNextWeekdayOccurrence = (
  fromDate: Date,
  daysOfWeek: number[],
  interval: number
): Date => {
  const sortedDays = [...daysOfWeek].sort((a, b) => a - b);
  const currentDay = fromDate.getDay();
  const nextDate = new Date(fromDate);

  // Find the next day in the current week
  let foundInCurrentWeek = false;
  for (const day of sortedDays) {
    if (day > currentDay) {
      const daysToAdd = day - currentDay;
      nextDate.setDate(nextDate.getDate() + daysToAdd);
      foundInCurrentWeek = true;
      break;
    }
  }

  // If not found in current week, go to next week(s) and take the first day
  if (!foundInCurrentWeek) {
    const firstDay = sortedDays[0];
    const daysUntilNextWeek = 7 - currentDay + firstDay;
    const totalDays = daysUntilNextWeek + 7 * (interval - 1);
    nextDate.setDate(nextDate.getDate() + totalDays);
  } else if (interval > 1) {
    // If found in current week but interval > 1, we need to skip weeks
    // Actually, for interval > 1, we skip to the same day in interval weeks
    nextDate.setDate(nextDate.getDate() + 7 * (interval - 1));
  }

  return nextDate;
};

/**
 * Get the number of days in a month
 */
const getDaysInMonth = (date: Date): number => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
};

/**
 * Check if a year is a leap year
 */
const isLeapYear = (year: number): boolean => {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
};

/**
 * Check if a recurrence has reached its count limit
 */
export const hasReachedCountLimit = (completedCount: number, rule: RecurrenceRule): boolean => {
  if (rule.count === null || rule.count === undefined) {
    return false;
  }
  return completedCount >= rule.count;
};

/**
 * Get a human-readable description of the next occurrence
 */
export const getNextOccurrenceDescription = (
  nextDate: Date,
  _rule: RecurrenceRule
): string => {
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  // Reset times for comparison
  const nextDateOnly = new Date(nextDate);
  nextDateOnly.setHours(0, 0, 0, 0);
  const nowOnly = new Date(now);
  nowOnly.setHours(0, 0, 0, 0);
  const tomorrowOnly = new Date(tomorrow);
  tomorrowOnly.setHours(0, 0, 0, 0);

  if (nextDateOnly.getTime() === nowOnly.getTime()) {
    return 'Today';
  }
  if (nextDateOnly.getTime() === tomorrowOnly.getTime()) {
    return 'Tomorrow';
  }

  // Check if within this week
  const daysUntil = Math.floor(
    (nextDateOnly.getTime() - nowOnly.getTime()) / (1000 * 60 * 60 * 24)
  );
  if (daysUntil < 7) {
    return nextDate.toLocaleDateString('en-NZ', { weekday: 'long' });
  }

  return nextDate.toLocaleDateString('en-NZ', {
    month: 'short',
    day: 'numeric',
  });
};
